/* eslint-disable react/no-unknown-property */
import {
  Button,
  Icon,
  IconButton,
  TextCaption,
} from '@pypestream/design-system';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
  JUDGE_LIMITS,
  DEFAULT_JUDGE_MODEL,
  RUBRIC_AUTO_FAIL,
  configHash,
  guardrailsConfig,
  isStandardRubric,
  rubricCriteriaOf,
  topicLabel,
  type JudgeCheck,
  type JudgeConfig,
  type JudgeExpected,
  type JudgeKind,
  type JudgeResult,
  type JudgeTopic,
  type ScoreCriterion,
} from '../../utils/judges';
import { PanelHead } from './validate-panels';
import {
  requestJudge,
  requestJudgeImport,
  requestJudgeSuggest,
} from './validate-api';
import {
  agreement,
  exampleFromRun,
  exampleFromSample,
  extraTestJudges,
  newJudge,
  parseTeamValue,
  resultReasons,
  resultValue,
  sampleFromRun,
  valueText,
  type LiveAlert,
  type LiveSample,
  type RunJudgeEntry,
  type TeamSample,
  type ValidateJudge,
} from './validate-judges';
import { relativeTime, type ValidateRun } from './validate-runs';
import { runWithConcurrency } from './validate-suites';
import './validate-judges.scss';

/**
 * Judges: LLM-as-judge per solution, inside Validate (prototype slice 5,
 * "VALIDATE · JUDGES"). The list and a judge's page live in the main column;
 * New judge opens in the right-hand panel, like New suite. A run's detail
 * shows what the solution's other judges made of it (`RunJudges`).
 */

const KIND_LABEL: Record<JudgeKind, string> = {
  rubric: 'Score out of 100',
  score: 'Score out of 100',
  check: 'Pass or fail',
  tag: 'Tags',
};

const KIND_ICON: Record<JudgeKind, string> = {
  rubric: 'stars',
  score: 'stars',
  check: 'warning',
  tag: 'elements-text',
};

const SAMPLE_LABEL: Record<LiveSample, string> = {
  all: 'Every conversation',
  '4': '1 in 4 conversations',
  '10': '1 in 10 conversations',
};

const ALERT_LABEL: Record<LiveAlert, string> = {
  off: 'Only show it in Observe',
  slack: 'Post to the project’s Slack channel',
  email: 'Email the project’s admins',
};

const scored = (k: JudgeKind) => k === 'rubric' || k === 'score';

/** A run a judge can read: finished, with at least one bot reply. */
const judgeableRuns = (runs: ValidateRun[]) =>
  runs.filter(
    (r) =>
      r.score.status !== 'pending' &&
      r.transcript.some((t) => t.role === 'bot' && !t.system)
  );

const ResultValue: FC<{ config: JudgeConfig; value: JudgeExpected }> = ({
  config,
  value,
}) => {
  if (config.kind === 'tag') {
    return <span className="c-judges-value">{String(value)}</span>;
  }
  const pass =
    config.kind === 'check' ? value === 'pass' : Number(value) >= config.bar;
  return (
    <span className={`c-judges-value ${pass ? 'is-pass' : 'is-fail'}`}>
      {valueText(config.kind, value)}
    </span>
  );
};

/* ---- List ------------------------------------------------------------------ */

export const JudgesView: FC<{
  judges: ValidateJudge[];
  onOpen: (id: string) => void;
  onNew: () => void;
}> = ({ judges, onOpen, onNew }) => (
  <div className="c-judges" test-id="bobcat-validate-judges">
    <div className="c-validate-runs-bar">
      <span className="c-validate-runs-bar__meta">
        Judges score conversations: every test run, and live conversations once
        live scoring is connected. Quality score is the rubric every solution
        starts with. Edit it to fit, or add more.
      </span>
      <Button
        size="small"
        variant="primary"
        test-id="bobcat-validate-new-judge"
        onClick={onNew}
      >
        <Icon name="add" slot="prefix" size="xsmall" />
        New judge
      </Button>
    </div>
    {judges.map((j) => {
      const a = agreement(j.config, j.samples);
      return (
        <button
          key={j.id}
          type="button"
          className="c-judges-card"
          test-id="bobcat-validate-judge"
          onClick={() => onOpen(j.id)}
        >
          <span className={`c-judges-mark c-judges-mark--${j.config.kind}`}>
            <Icon name={KIND_ICON[j.config.kind] as never} size="small" />
          </span>
          <span className="c-judges-card__body">
            <b>{j.config.name}</b>
            <span>{j.description}</span>
            <span className="c-judges-chips">
              <span className="c-judges-chip">
                {KIND_LABEL[j.config.kind]}
                {scored(j.config.kind) ? ` · passes at ${j.config.bar}` : ''}
              </span>
              <span className={`c-judges-chip ${j.tests ? 'is-on' : ''}`}>
                Test runs {j.tests ? 'on' : 'off'}
              </span>
              <span className={`c-judges-chip ${j.live ? 'is-on' : ''}`}>
                Live{' '}
                {j.live
                  ? j.sample === 'all'
                    ? 'on'
                    : `1 in ${j.sample}`
                  : 'off'}
              </span>
              <span className="c-judges-chip">v{j.version}</span>
            </span>
          </span>
          <span className="c-judges-card__agree">
            {a.rate === null ? (
              <>
                <b>Not checked</b>
                {a.total
                  ? `${a.total} scored by your team — check it`
                  : 'Score a few runs to check it'}
              </>
            ) : (
              <>
                <b>{a.rate}% agrees</b>
                with your team, on {a.checked}
                {a.stale ? ` (${a.stale} to re-check)` : ''}
              </>
            )}
          </span>
        </button>
      );
    })}
  </div>
);

/* ---- Small editors ----------------------------------------------------------- */

const ListEditor: FC<{
  items: string[];
  placeholder: string;
  addLabel: string;
  testId: string;
  numbered?: boolean;
  highlight?: string | null;
  onChange: (items: string[]) => void;
}> = ({
  items,
  placeholder,
  addLabel,
  testId,
  numbered,
  highlight,
  onChange,
}) => {
  const [text, setText] = useState('');
  const add = () => {
    const t = text.trim();
    if (!t) return;
    onChange([...items, t]);
    setText('');
  };
  return (
    <div className="c-judges-list" test-id={testId}>
      {items.length > 0 && (
        <ol className={numbered ? 'is-numbered' : ''}>
          {items.map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li
              key={`${i}-${item}`}
              className={item === highlight ? 'is-new' : ''}
            >
              {numbered && <span className="c-judges-n">{i + 1}</span>}
              <span>{item}</span>
              <IconButton
                name="close"
                size="small"
                variant="tertiary"
                aria-label="Remove"
                onClick={() => onChange(items.filter((_, k) => k !== i))}
              />
            </li>
          ))}
        </ol>
      )}
      <div className="c-judges-add">
        <input
          value={text}
          placeholder={placeholder}
          test-id={`${testId}-input`}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') add();
          }}
        />
        <Button
          size="small"
          variant="secondary"
          disabled={!text.trim()}
          onClick={add}
        >
          {addLabel}
        </Button>
      </div>
    </div>
  );
};

/** What a person gives a conversation: a score, pass/fail, or a topic. */
const TeamValueInput: FC<{
  config: JudgeConfig;
  value: string;
  testId: string;
  onChange: (v: string) => void;
}> = ({ config, value, testId, onChange }) => {
  if (config.kind === 'check') {
    return (
      <select
        className="c-validate-select"
        value={value}
        test-id={testId}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Pass or fail?</option>
        <option value="pass">Pass</option>
        <option value="fail">Fail</option>
      </select>
    );
  }
  if (config.kind === 'tag') {
    return (
      <select
        className="c-validate-select"
        value={value}
        test-id={testId}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Which topic?</option>
        {config.topics.flatMap((t) => [
          <option key={t.name} value={t.name}>
            {t.name}
          </option>,
          ...t.subs.map((s) => (
            <option key={`${t.name}›${s}`} value={`${t.name} › ${s}`}>
              {t.name} › {s}
            </option>
          )),
        ])}
      </select>
    );
  }
  return (
    <input
      type="number"
      min={0}
      max={100}
      className="c-judges-score-input"
      value={value}
      placeholder="0–100"
      test-id={testId}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

/** Pick a run and give it a value — for team scores and for examples. */
const FromRunForm: FC<{
  config: JudgeConfig;
  runs: ValidateRun[];
  withNote?: boolean;
  submitLabel: string;
  testId: string;
  onSubmit: (run: ValidateRun, value: JudgeExpected, note: string) => void;
  onCancel: () => void;
}> = ({ config, runs, withNote, submitLabel, testId, onSubmit, onCancel }) => {
  const choices = judgeableRuns(runs);
  const [runId, setRunId] = useState(choices[0]?.id ?? '');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const parsed = parseTeamValue(config, value);
  const run = choices.find((r) => r.id === runId);
  if (!choices.length) {
    return (
      <div className="c-judges-form">
        <TextCaption>
          No finished runs yet. Run a scenario or a suite, or end a Test chat,
          then score it here.
        </TextCaption>
        <Button size="small" variant="ghost" onClick={onCancel}>
          Close
        </Button>
      </div>
    );
  }
  return (
    <div className="c-judges-form" test-id={testId}>
      <label className="c-validate-field">
        <span>Conversation</span>
        <select
          className="c-validate-select"
          value={runId}
          test-id={`${testId}-run`}
          onChange={(e) => setRunId(e.target.value)}
        >
          {choices.slice(0, 60).map((r) => (
            <option key={r.id} value={r.id}>
              {(r.suite?.caseName || r.title).slice(0, 80)} ·{' '}
              {r.kind === 'scenario' ? 'Scenario' : 'Test'}
            </option>
          ))}
        </select>
      </label>
      {run && (
        <ol className="c-judges-preview">
          {run.transcript
            .filter((t) => !t.system && t.text.trim())
            .slice(-6)
            .map((t, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}>
                <b>{t.role === 'bot' ? 'Bot' : 'Customer'}:</b>{' '}
                {t.text.slice(0, 220)}
              </li>
            ))}
        </ol>
      )}
      <label className="c-validate-field">
        <span>
          {config.kind === 'check'
            ? 'Your verdict'
            : config.kind === 'tag'
              ? 'Your topic'
              : 'Your score, out of 100'}
        </span>
        <TeamValueInput
          config={config}
          value={value}
          testId={`${testId}-value`}
          onChange={setValue}
        />
      </label>
      {withNote && (
        <label className="c-validate-field">
          <span>Why</span>
          <input
            value={note}
            placeholder="e.g. Asking for the policy number first is required, not a delay."
            test-id={`${testId}-note`}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      )}
      <div className="c-judges-form__actions">
        <Button
          size="small"
          variant="primary"
          disabled={!run || parsed === null}
          test-id={`${testId}-submit`}
          onClick={() => run && parsed !== null && onSubmit(run, parsed, note)}
        >
          {submitLabel}
        </Button>
        <Button size="small" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

/* ---- What it scores ----------------------------------------------------------- */

/** What's wrong with a judge's criteria as edited, or null when it can be saved. */
export const criteriaProblem = (config: JudgeConfig): string | null => {
  if (config.kind === 'rubric') {
    const list = rubricCriteriaOf(config);
    if (list.length < 2) return 'The rubric needs at least two criteria.';
    if (list.some((c) => !c.label.trim() || !c.definition.trim()))
      return 'Every criterion needs a name and what it means.';
    if (list.some((c) => !c.weight || c.weight < 1))
      return 'Every criterion needs at least 1 point.';
    const total = list.reduce((sum, c) => sum + (c.weight ?? 0), 0);
    if (total !== 100)
      return `The weights add up to ${total}. Make them add up to 100.`;
    return null;
  }
  if (config.kind === 'score') {
    if (!config.criteria.length) return 'Add at least one category.';
    if (config.criteria.some((c) => !c.label.trim() || c.scores.length < 2))
      return 'Every category needs a name and at least two scores it can give.';
  }
  return null;
};

const parseScores = (text: string): number[] =>
  [
    ...new Set(
      text
        .split(/[\s,]+/)
        .map(Number)
        .filter((n) => Number.isFinite(n) && n >= 0 && n <= 100)
        .map(Math.round)
    ),
  ]
    .sort((a, b) => a - b)
    .slice(0, JUDGE_LIMITS.scoresPerCriterion);

const newKey = (list: ScoreCriterion[]) => {
  let i = list.length + 1;
  while (list.some((c) => c.key === `criterion_${i}`)) i += 1;
  return `criterion_${i}`;
};

/**
 * The criteria a scored judge gives points for. On the Quality score judge
 * they're the rubric's — each scored 1–5 against its levels, worth its
 * weight of the 100 points — and every part of them can be changed. On a
 * Score judge, each category and the only scores it may give.
 */
const CriteriaEditor: FC<{
  config: JudgeConfig;
  onChange: (criteria: ScoreCriterion[]) => void;
}> = ({ config, onChange }) => {
  const rubric = config.kind === 'rubric';
  const list = rubric ? rubricCriteriaOf(config) : config.criteria;
  // Score inputs keep what's typed ("0, 15,") until it parses.
  const [scoreText, setScoreText] = useState<Record<string, string>>({});
  const commit = (next: ScoreCriterion[]) =>
    onChange(rubric && isStandardRubric(next) ? [] : next);
  const set = (i: number, patch: Partial<ScoreCriterion>) =>
    commit(list.map((c, k) => (k === i ? { ...c, ...patch } : c)));
  const remove = (i: number) => commit(list.filter((_, k) => k !== i));
  const add = () =>
    commit([
      ...list,
      rubric
        ? {
            key: newKey(list),
            label: '',
            short: '',
            group: '',
            definition: '',
            scores: [1, 2, 3, 4, 5],
            levels: ['', '', '', '', ''],
            weight: 0,
          }
        : {
            key: newKey(list),
            label: '',
            group: '',
            definition: '',
            scores: [0, 10],
          },
    ]);
  const total = list.reduce(
    (sum, c) => sum + (rubric ? (c.weight ?? 0) : Math.max(0, ...c.scores)),
    0
  );
  const max = rubric ? JUDGE_LIMITS.rubricCriteria : JUDGE_LIMITS.criteria;
  const min = rubric ? 2 : 1;

  return (
    <div className="c-judges-crit" test-id="bobcat-validate-judge-criteria">
      {list.map((c, i) => (
        <details
          // eslint-disable-next-line react/no-array-index-key
          key={`${c.key}-${i}`}
          className="c-judges-crit__item"
          open={!c.label}
        >
          <summary>
            <span className="c-judges-crit__name">
              <b>{c.label || 'New criterion'}</b>
              {c.group && <span className="is-muted">{c.group}</span>}
            </span>
            <span className="c-judges-crit__points">
              {rubric
                ? `${c.weight ?? 0} pts`
                : `scores ${c.scores.join(', ') || '—'}`}
            </span>
            <Icon name="chevron-down" size="xsmall" />
          </summary>
          <div className="c-judges-crit__body">
            <div className="c-judges-crit__row">
              <label className="c-validate-field">
                <span>Name</span>
                <input
                  value={c.label}
                  maxLength={JUDGE_LIMITS.name}
                  test-id="bobcat-validate-judge-criterion-label"
                  onChange={(e) => set(i, { label: e.target.value })}
                />
              </label>
              {rubric && (
                <label className="c-validate-field">
                  <span>Short name</span>
                  <input
                    value={c.short ?? ''}
                    maxLength={24}
                    placeholder={c.label.slice(0, 24)}
                    onChange={(e) => set(i, { short: e.target.value })}
                  />
                </label>
              )}
              <label className="c-validate-field">
                <span>Group</span>
                <input
                  value={c.group}
                  maxLength={JUDGE_LIMITS.name}
                  onChange={(e) => set(i, { group: e.target.value })}
                />
              </label>
              {rubric ? (
                <label className="c-validate-field c-judges-crit__narrow">
                  <span>Points</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={c.weight ?? 0}
                    test-id="bobcat-validate-judge-criterion-weight"
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      set(i, {
                        weight: Number.isFinite(n)
                          ? Math.min(100, Math.max(0, Math.round(n)))
                          : 0,
                      });
                    }}
                  />
                </label>
              ) : (
                <label className="c-validate-field">
                  <span>Scores it can give</span>
                  <input
                    value={scoreText[c.key] ?? c.scores.join(', ')}
                    placeholder="0, 15, 30"
                    test-id="bobcat-validate-judge-criterion-scores"
                    onChange={(e) => {
                      const text = e.target.value;
                      setScoreText((t) => ({ ...t, [c.key]: text }));
                      set(i, { scores: parseScores(text) });
                    }}
                    onBlur={() =>
                      setScoreText((t) => {
                        const { [c.key]: _drop, ...rest } = t;
                        return rest;
                      })
                    }
                  />
                </label>
              )}
            </div>
            <label className="c-validate-field">
              <span>What it means</span>
              <textarea
                rows={2}
                value={c.definition}
                maxLength={JUDGE_LIMITS.text}
                test-id="bobcat-validate-judge-criterion-definition"
                onChange={(e) => set(i, { definition: e.target.value })}
              />
            </label>
            {rubric && (
              <div className="c-judges-crit__levels">
                {(c.levels ?? ['', '', '', '', ''])
                  .map((level, k) => ({ level, k }))
                  .reverse()
                  .map(({ level, k }) => (
                    <label key={k} className="c-judges-crit__level">
                      <span className={`c-validate-lv c-validate-lv--${k + 1}`}>
                        {k + 1}
                      </span>
                      <input
                        value={level}
                        maxLength={JUDGE_LIMITS.text}
                        placeholder={`What a ${k + 1} looks like`}
                        onChange={(e) => {
                          const levels = [
                            ...(c.levels ?? ['', '', '', '', '']),
                          ];
                          levels[k] = e.target.value;
                          set(i, { levels });
                        }}
                      />
                    </label>
                  ))}
              </div>
            )}
            <div className="c-judges-crit__actions">
              <Button
                size="small"
                variant="ghost"
                disabled={list.length <= min}
                test-id="bobcat-validate-judge-criterion-remove"
                onClick={() => remove(i)}
              >
                Remove
              </Button>
            </div>
          </div>
        </details>
      ))}
      <div className="c-judges-crit__foot">
        <Button
          size="small"
          variant="secondary"
          disabled={list.length >= max}
          test-id="bobcat-validate-judge-criterion-add"
          onClick={add}
        >
          <Icon name="add" slot="prefix" size="xsmall" />
          {rubric ? 'Add criterion' : 'Add category'}
        </Button>
        <span
          className="c-judges-crit__total"
          test-id="bobcat-validate-judge-criteria-total"
        >
          {rubric
            ? `${total} of 100 points`
            : `Top scores add up to ${total}; the total is scaled to 100`}
        </span>
        {criteriaProblem(config) && (
          <span className="c-judges-crit__total is-off" role="alert">
            {criteriaProblem(config)}
          </span>
        )}
        {rubric && config.criteria.length > 0 && (
          <Button
            size="small"
            variant="ghost"
            test-id="bobcat-validate-judge-criteria-reset"
            onClick={() => onChange([])}
          >
            Back to the standard rubric
          </Button>
        )}
      </div>
    </div>
  );
};

const ChecksEditor: FC<{
  checks: JudgeCheck[];
  onChange: (checks: JudgeCheck[]) => void;
}> = ({ checks, onChange }) => {
  const set = (i: number, patch: Partial<JudgeCheck>) =>
    onChange(checks.map((c, k) => (k === i ? { ...c, ...patch } : c)));
  return (
    <div className="c-judges-checks">
      {checks.map((c, i) => (
        <div key={c.name} className="c-judges-box c-judges-check">
          <h4>{c.name}</h4>
          <span className="c-judges-eyebrow">Fails if</span>
          <ListEditor
            items={c.fails}
            placeholder="Something that fails it"
            addLabel="Add"
            testId="bobcat-validate-judge-check-fails"
            onChange={(fails) => set(i, { fails })}
          />
          <span className="c-judges-eyebrow">Never a failure</span>
          <ListEditor
            items={c.never}
            placeholder="Something that never fails it"
            addLabel="Add"
            testId="bobcat-validate-judge-check-never"
            onChange={(never) => set(i, { never })}
          />
        </div>
      ))}
    </div>
  );
};

const TopicsEditor: FC<{
  topics: JudgeTopic[];
  onChange: (topics: JudgeTopic[]) => void;
}> = ({ topics, onChange }) => {
  const [name, setName] = useState('');
  return (
    <div className="c-judges-box c-judges-topics">
      {topics.map((t, i) => (
        <div key={t.name} className="c-judges-topic">
          <b>{t.name}</b>
          <input
            value={t.subs.join(', ')}
            placeholder="Subtopics, separated by commas"
            aria-label={`${t.name} subtopics`}
            onChange={(e) =>
              onChange(
                topics.map((x, k) =>
                  k === i
                    ? {
                        ...x,
                        subs: e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean),
                      }
                    : x
                )
              )
            }
          />
          <IconButton
            name="close"
            size="small"
            variant="tertiary"
            aria-label={`Remove ${t.name}`}
            disabled={topics.length === 1}
            onClick={() => onChange(topics.filter((_, k) => k !== i))}
          />
        </div>
      ))}
      <div className="c-judges-add">
        <input
          value={name}
          placeholder="Add a topic, e.g. Billing"
          test-id="bobcat-validate-judge-topic-input"
          onChange={(e) => setName(e.target.value)}
        />
        <Button
          size="small"
          variant="secondary"
          disabled={
            !name.trim() ||
            topics.some(
              (t) => t.name.toLowerCase() === name.trim().toLowerCase()
            )
          }
          onClick={() => {
            onChange([...topics, { name: name.trim(), subs: [] }]);
            setName('');
          }}
        >
          Add topic
        </Button>
      </div>
    </div>
  );
};

/* ---- A judge's page --------------------------------------------------------- */

type RowState = { busy?: boolean; error?: string };

export const JudgeDetail: FC<{
  judge: ValidateJudge;
  runs: ValidateRun[];
  makeId: () => string;
  onBack: () => void;
  onSave: (judge: ValidateJudge) => void;
  /** Team scores and checks save straight away; they aren't part of a version. */
  onSamples: (id: string, samples: TeamSample[]) => void;
  onDelete: (id: string) => void;
}> = ({ judge, runs, makeId, onBack, onSave, onSamples, onDelete }) => {
  const [draft, setDraft] = useState<ValidateJudge>(judge);
  const [savedNote, setSavedNote] = useState<number | null>(null);
  const [adding, setAdding] = useState<'sample' | 'example' | null>(null);
  const [newRule, setNewRule] = useState<string | null>(null);
  const [checking, setChecking] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => abort.current?.abort(), []);

  // A different judge, or a new version saved: start from what's saved.
  useEffect(() => {
    setDraft(judge);
    setNewRule(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [judge.id, judge.version]);

  const { config } = draft;
  const { samples } = judge;
  const dirty = useMemo(
    () =>
      JSON.stringify({ ...draft, samples: [] }) !==
      JSON.stringify({ ...judge, samples: [] }),
    [draft, judge]
  );
  const a = agreement(config, samples);
  const hash = configHash(config);
  const setConfig = (patch: Partial<JudgeConfig>) =>
    setDraft((d) => ({ ...d, config: { ...d.config, ...patch } }));
  const set = (patch: Partial<ValidateJudge>) =>
    setDraft((d) => ({ ...d, ...patch }));

  const updateSample = (id: string, patch: Partial<TeamSample>) =>
    onSamples(
      judge.id,
      samples.map((s) => (s.id === id ? { ...s, ...patch } : s))
    );

  const checkAgain = async () => {
    abort.current?.abort();
    const ctrl = new AbortController();
    abort.current = ctrl;
    const todo = samples.filter((s) => s.judged?.hash !== hash);
    const list = todo.length ? todo : samples;
    setChecking({ done: 0, total: list.length });
    const results = new Map<string, TeamSample['judged']>();
    const errors: Record<string, RowState> = {};
    await runWithConcurrency(
      list,
      (s) =>
        requestJudge(
          {
            judge: config,
            transcript: s.transcript,
            scenario: s.scenario,
            expectation: s.expectation,
          },
          ctrl.signal
        ),
      {
        limit: 3,
        signal: ctrl.signal,
        onSettled: (r, i) => {
          const s = list[i];
          if (r.status === 'fulfilled') {
            results.set(s.id, { hash, result: r.value, at: Date.now() });
          } else {
            errors[s.id] = {
              error:
                r.reason instanceof Error
                  ? r.reason.message
                  : 'The judge failed.',
            };
          }
          setChecking((c) => (c ? { ...c, done: c.done + 1 } : c));
        },
      }
    );
    if (ctrl.signal.aborted) return;
    setChecking(null);
    setRows(errors);
    onSamples(
      judge.id,
      samples.map((s) =>
        results.has(s.id) ? { ...s, judged: results.get(s.id) } : s
      )
    );
  };

  const suggest = async (s: TeamSample) => {
    if (!s.judged) return;
    setRows((r) => ({ ...r, [s.id]: { busy: true } }));
    try {
      const out = await requestJudgeSuggest({
        judge: config,
        transcript: s.transcript,
        team: s.team,
        result: {
          value: resultValue(s.judged.result),
          summary: s.judged.result.summary,
          reasons: resultReasons(s.judged.result),
        },
      });
      updateSample(s.id, {
        suggestion: { hash, rule: out.rule, why: out.why },
      });
      setRows((r) => ({ ...r, [s.id]: {} }));
    } catch (err) {
      setRows((r) => ({
        ...r,
        [s.id]: {
          error:
            err instanceof Error ? err.message : 'No suggestion came back.',
        },
      }));
    }
  };

  const addRule = (rule: string) => {
    if (!config.rules.includes(rule))
      setConfig({ rules: [...config.rules, rule] });
    setNewRule(rule);
  };

  return (
    <div
      className="c-judges c-judges-detail"
      test-id="bobcat-validate-judge-detail"
    >
      <button type="button" className="c-judges-back" onClick={onBack}>
        <Icon name="chevron-left" size="xsmall" />
        All judges
      </button>
      <div className="c-judges-head">
        <div>
          {config.kind === 'rubric' ? (
            <h2>{config.name}</h2>
          ) : (
            <input
              className="c-judges-name"
              value={config.name}
              aria-label="Judge name"
              test-id="bobcat-validate-judge-name"
              onChange={(e) => setConfig({ name: e.target.value.slice(0, 80) })}
            />
          )}
          <p>{draft.description}</p>
          <span className="c-judges-chips">
            <span className="c-judges-chip">
              <Icon name={KIND_ICON[config.kind] as never} size="xsmall" />
              {KIND_LABEL[config.kind]}
            </span>
            <span className="c-judges-chip">
              v{judge.version}
              {judge.updatedAt
                ? ` · saved ${relativeTime(judge.updatedAt, Date.now())}`
                : ''}
            </span>
          </span>
        </div>
        <div className="c-judges-head__actions">
          {config.kind !== 'rubric' &&
            (confirmDelete ? (
              <>
                <TextCaption>Delete this judge?</TextCaption>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => onDelete(judge.id)}
                >
                  Delete
                </Button>
                <Button
                  size="small"
                  variant="ghost"
                  onClick={() => setConfirmDelete(false)}
                >
                  Keep
                </Button>
              </>
            ) : (
              <IconButton
                name="delete"
                size="small"
                variant="tertiary"
                aria-label="Delete judge"
                test-id="bobcat-validate-judge-delete"
                onClick={() => setConfirmDelete(true)}
              />
            ))}
          {dirty && (
            <Button
              size="small"
              variant="ghost"
              onClick={() => setDraft(judge)}
            >
              Discard changes
            </Button>
          )}
          <Button
            size="small"
            variant="primary"
            disabled={
              !dirty || !config.name.trim() || criteriaProblem(config) !== null
            }
            test-id="bobcat-validate-judge-save"
            onClick={() => {
              onSave({
                ...draft,
                samples,
                config: { ...config, name: config.name.trim() },
              });
              setSavedNote(judge.version + 1);
            }}
          >
            Save as v{judge.version + 1}
          </Button>
        </div>
      </div>

      {savedNote === judge.version && !dirty && (
        <div className="c-judges-saved" test-id="bobcat-validate-judge-saved">
          <Icon name="check" size="xsmall" />
          Saved as v{judge.version}.{' '}
          {config.kind === 'rubric'
            ? 'Every Test reply, scenario and suite case is scored with it from now on; earlier scores stay as they were.'
            : 'Runs judged from now on use it; earlier results stay as they were.'}
        </div>
      )}

      {/* Where it runs */}
      <section className="c-judges-sec">
        <h3>Where it runs</h3>
        <div className="c-judges-box c-judges-where">
          <label className="c-judges-switch">
            <span>
              Test runs and suites
              <small>
                {config.kind === 'rubric'
                  ? 'Always on: this is what passes or fails a run.'
                  : 'Each finished conversation in Test, every scenario and every suite case.'}
              </small>
            </span>
            <input
              type="checkbox"
              checked={draft.tests}
              disabled={config.kind === 'rubric'}
              test-id="bobcat-validate-judge-tests"
              onChange={(e) => set({ tests: e.target.checked })}
            />
          </label>
          <label className="c-judges-switch">
            <span>
              Live conversations
              <small>
                Each conversation after it ends, on the published version. Live
                scoring isn’t connected yet: this is saved for when it is.
              </small>
            </span>
            <input
              type="checkbox"
              checked={draft.live}
              test-id="bobcat-validate-judge-live"
              onChange={(e) => set({ live: e.target.checked })}
            />
          </label>
          {draft.live ? (
            <label className="c-validate-field">
              <span>How many live conversations</span>
              <select
                className="c-validate-select"
                value={draft.sample}
                onChange={(e) => set({ sample: e.target.value as LiveSample })}
              >
                {(Object.keys(SAMPLE_LABEL) as LiveSample[]).map((k) => (
                  <option key={k} value={k}>
                    {SAMPLE_LABEL[k]}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <span />
          )}
          {draft.live && config.kind === 'check' && (
            <label className="c-validate-field">
              <span>When a live conversation fails</span>
              <select
                className="c-validate-select"
                value={draft.alert}
                onChange={(e) => set({ alert: e.target.value as LiveAlert })}
              >
                {(Object.keys(ALERT_LABEL) as LiveAlert[]).map((k) => (
                  <option key={k} value={k}>
                    {ALERT_LABEL[k]}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </section>

      {/* Agreement with the team */}
      <section
        className="c-judges-sec"
        test-id="bobcat-validate-judge-agreement"
      >
        <h3>Does it agree with your team?</h3>
        <p>
          Score some conversations yourselves, then check the judge against you.
          Where it disagrees, add a rule or an example and check again.
          {scored(config.kind)
            ? ` It agrees when you both pass or both fail it, within 10 points.`
            : ''}
        </p>
        <div className="c-judges-box">
          <div className="c-judges-agree">
            <span
              className={`c-judges-big ${
                a.rate === null ? '' : a.rate >= 90 ? 'is-ok' : 'is-low'
              }`}
              test-id="bobcat-validate-judge-rate"
            >
              {a.rate === null ? '—' : `${a.rate}%`}
            </span>
            <span className="c-judges-agree__text">
              {a.total === 0 ? (
                'No conversations scored by your team yet.'
              ) : a.checked === 0 ? (
                `${a.total} scored by your team. Check the judge to compare.`
              ) : (
                <>
                  <b>
                    Agrees on {a.agree} of {a.checked}.
                  </b>{' '}
                  {a.gap !== null
                    ? `On average ${a.gap} points from your team. `
                    : ''}
                  {a.rate !== null && a.rate >= 90
                    ? 'Close enough to trust for trends and alerts.'
                    : 'Below 90%: worth fixing before you rely on it.'}
                  {a.stale
                    ? ` ${a.stale} ${a.stale === 1 ? 'was' : 'were'} judged before your latest change: check again.`
                    : ''}
                </>
              )}
            </span>
            <span className="c-judges-agree__actions">
              <Button
                size="small"
                variant="ghost"
                test-id="bobcat-validate-judge-add-sample"
                onClick={() => setAdding(adding === 'sample' ? null : 'sample')}
              >
                <Icon name="add" slot="prefix" size="xsmall" />
                Score a conversation
              </Button>
              <Button
                size="small"
                variant={a.stale ? 'primary' : 'secondary'}
                disabled={!samples.length || !!checking}
                test-id="bobcat-validate-judge-check"
                onClick={() => void checkAgain()}
              >
                <Icon name="refresh" slot="prefix" size="xsmall" />
                {checking
                  ? `Checking ${checking.done} of ${checking.total}…`
                  : a.checked
                    ? 'Check again'
                    : 'Check the judge'}
              </Button>
            </span>
          </div>
          {adding === 'sample' && (
            <FromRunForm
              config={config}
              runs={runs}
              submitLabel="Add"
              testId="bobcat-validate-judge-sample-form"
              onSubmit={(run, value) => {
                onSamples(judge.id, [
                  sampleFromRun(makeId(), run, value),
                  ...samples,
                ]);
                setAdding(null);
              }}
              onCancel={() => setAdding(null)}
            />
          )}
          {samples.length > 0 && (
            <div className="c-judges-table c-judges-table--samples">
              <div className="c-judges-table__row c-judges-table__row--head">
                <span>Conversation</span>
                <span>Your team</span>
                <span>Judge</span>
                <span>Why</span>
                <span />
              </div>
              {samples.map((s) => {
                const fresh = s.judged?.hash === hash ? s.judged : undefined;
                const value = fresh ? resultValue(fresh.result) : null;
                const disagrees = !!fresh && a.disagreements.includes(s);
                const sugg =
                  s.suggestion?.hash === hash ? s.suggestion : undefined;
                const row = rows[s.id] ?? {};
                return (
                  <div
                    key={s.id}
                    className={`c-judges-table__row ${disagrees ? 'is-off' : ''}`}
                    test-id="bobcat-validate-judge-sample"
                  >
                    <span>{s.title}</span>
                    <span>
                      <ResultValue config={config} value={s.team} />
                    </span>
                    <span>
                      {value !== null ? (
                        <ResultValue config={config} value={value} />
                      ) : (
                        <span className="is-muted">
                          {s.judged ? 'Out of date' : 'Not checked'}
                        </span>
                      )}
                    </span>
                    <span className="c-judges-why">
                      {row.error && (
                        <span className="is-error">{row.error}</span>
                      )}
                      {fresh
                        ? fresh.result.summary || (disagrees ? '' : 'Agrees.')
                        : ''}
                      {disagrees && sugg && (
                        <em>
                          <span>Suggested rule</span>
                          {sugg.rule}
                        </em>
                      )}
                    </span>
                    <span className="c-judges-row-actions">
                      {disagrees && !sugg && (
                        <Button
                          size="small"
                          variant="secondary"
                          disabled={row.busy}
                          test-id="bobcat-validate-judge-suggest"
                          onClick={() => void suggest(s)}
                        >
                          {row.busy ? 'Thinking…' : 'Suggest a rule'}
                        </Button>
                      )}
                      {disagrees && sugg && (
                        <Button
                          size="small"
                          variant="primary"
                          disabled={config.rules.includes(sugg.rule)}
                          test-id="bobcat-validate-judge-add-rule"
                          onClick={() => addRule(sugg.rule)}
                        >
                          {config.rules.includes(sugg.rule)
                            ? 'Rule added'
                            : 'Add the rule'}
                        </Button>
                      )}
                      {disagrees && config.kind !== 'tag' && (
                        <button
                          type="button"
                          className="c-validate-link"
                          onClick={() =>
                            setConfig({
                              examples: [
                                ...config.examples,
                                exampleFromSample(
                                  makeId(),
                                  s,
                                  fresh?.result.summary ?? ''
                                ),
                              ],
                            })
                          }
                        >
                          Add as example
                        </button>
                      )}
                      {disagrees && value !== null && (
                        <button
                          type="button"
                          className="c-validate-link"
                          onClick={() => updateSample(s.id, { team: value })}
                        >
                          The judge is right
                        </button>
                      )}
                      <IconButton
                        name="close"
                        size="small"
                        variant="tertiary"
                        aria-label="Remove from the check"
                        onClick={() =>
                          onSamples(
                            judge.id,
                            samples.filter((x) => x.id !== s.id)
                          )
                        }
                      />
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* What it scores */}
      <section className="c-judges-sec">
        {scored(config.kind) && (
          <>
            <h3>What it scores</h3>
            <p>
              {config.kind === 'rubric'
                ? config.criteria.length
                  ? 'Edited from the Generative AI Evaluation Rubric. Each reply is scored 1–5 on each criterion, worth its points, out of 100. An auto-fail scores 0. Scores aren’t comparable with solutions on the standard rubric.'
                  : 'The Generative AI Evaluation Rubric: each reply is scored 1–5 on five criteria, 20 points each, out of 100. An auto-fail scores 0. Change any of it to fit this solution.'
                : 'Each category and the only scores it may give. The total is out of 100.'}
            </p>
            <CriteriaEditor
              config={config}
              onChange={(criteria) => setConfig({ criteria })}
            />
            {config.kind === 'rubric' && (
              <p className="c-judges-autofail">
                <Icon name="warning" size="xsmall" />
                Auto-fails:{' '}
                {RUBRIC_AUTO_FAIL.map((g) => g.items.join(', ')).join('; ')}.
              </p>
            )}
            <label className="c-judges-bar">
              A conversation passes at
              <input
                type="number"
                min={0}
                max={100}
                value={config.bar}
                test-id="bobcat-validate-judge-bar"
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n))
                    setConfig({
                      bar: Math.min(100, Math.max(0, Math.round(n))),
                    });
                }}
              />
              <span className="is-muted">out of 100</span>
            </label>
          </>
        )}
        {config.kind === 'check' && (
          <>
            <h3>What fails a conversation</h3>
            <p>
              Each check, what fails it and what never does. The “never” lists
              are what keep it from crying wolf.
            </p>
            <div className="c-judges-posture">
              <b>Passes unless a check clearly fails.</b> If the judge is
              weighing whether something might be a failure, it isn’t.
            </div>
            <ChecksEditor
              checks={config.checks}
              onChange={(checks) => setConfig({ checks })}
            />
          </>
        )}
        {config.kind === 'tag' && (
          <>
            <h3>What it tags</h3>
            <p>
              One topic and subtopic per conversation, by these exact names, and
              the customer’s sentiment.
            </p>
            <TopicsEditor
              topics={config.topics}
              onChange={(topics) => setConfig({ topics })}
            />
          </>
        )}
      </section>

      {/* Rules */}
      <section className="c-judges-sec">
        <h3>Rules for this solution</h3>
        <p>
          What’s normal for this solution, so the judge doesn’t mark it down,
          and what’s worse than it looks.
        </p>
        <div className="c-judges-box">
          <ListEditor
            items={config.rules}
            numbered
            highlight={newRule}
            placeholder="e.g. Asking for the account number before any account detail is required, not a delay."
            addLabel="Add rule"
            testId="bobcat-validate-judge-rules"
            onChange={(rules) => setConfig({ rules })}
          />
        </div>
      </section>

      {/* Examples */}
      {config.kind !== 'tag' && (
        <section className="c-judges-sec">
          <h3>Examples</h3>
          <p>
            Conversations with the result your team gave them. The judge reads
            these every time; they’re the surest way to make it score like you.
          </p>
          {config.examples.length > 0 && (
            <div className="c-judges-examples">
              {config.examples.map((e) => (
                <div key={e.id} className="c-judges-example">
                  <div className="c-judges-example__head">
                    <b>{e.title}</b>
                    <ResultValue config={config} value={e.expected} />
                    <IconButton
                      name="close"
                      size="small"
                      variant="tertiary"
                      aria-label="Remove example"
                      onClick={() =>
                        setConfig({
                          examples: config.examples.filter(
                            (x) => x.id !== e.id
                          ),
                        })
                      }
                    />
                  </div>
                  <ol>
                    {e.lines.slice(-6).map((l, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={i}>
                        <b>{l.who === 'bot' ? 'Bot' : 'Customer'}:</b>{' '}
                        {l.text.slice(0, 200)}
                      </li>
                    ))}
                  </ol>
                  {e.note && <p>{e.note}</p>}
                </div>
              ))}
            </div>
          )}
          {adding === 'example' ? (
            <div className="c-judges-box">
              <FromRunForm
                config={config}
                runs={runs}
                withNote
                submitLabel="Add example"
                testId="bobcat-validate-judge-example-form"
                onSubmit={(run, value, note) => {
                  setConfig({
                    examples: [
                      ...config.examples,
                      exampleFromRun(makeId(), run, { expected: value, note }),
                    ],
                  });
                  setAdding(null);
                }}
                onCancel={() => setAdding(null)}
              />
            </div>
          ) : (
            <Button
              size="small"
              variant="ghost"
              test-id="bobcat-validate-judge-add-example"
              onClick={() => setAdding('example')}
            >
              <Icon name="add" slot="prefix" size="xsmall" />
              Add from a run
            </Button>
          )}
        </section>
      )}
    </div>
  );
};

/* ---- New judge ---------------------------------------------------------------- */

type NewKind = 'check' | 'tag' | 'import';

const NEW_OPTIONS: { kind: NewKind; label: string; help: string }[] = [
  {
    kind: 'check',
    label: 'Pass-or-fail checks',
    help: 'Guardrails: anything that should fail a conversation outright. Starts from the standard checks.',
  },
  {
    kind: 'tag',
    label: 'Topics and sentiment',
    help: 'Tags what each conversation was about and how the customer felt.',
  },
  {
    kind: 'import',
    label: 'Import a scorer prompt',
    help: 'Paste the prompt of an existing scorer. Its categories, scores, rules and examples come across for you to edit.',
  },
];

export const NewJudgePanel: FC<{
  makeId: () => string;
  onCreate: (judge: ValidateJudge) => void;
  onClose: () => void;
}> = ({ makeId, onCreate, onClose }) => {
  const [kind, setKind] = useState<NewKind>('check');
  const [prompt, setPrompt] = useState('');
  const [topic, setTopic] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => abort.current?.abort(), []);

  const topics = topic
    .split('\n')
    .map((t) => t.trim())
    .filter(Boolean);

  const create = async () => {
    setError(null);
    if (kind === 'check') {
      onCreate(
        newJudge(
          makeId(),
          { ...guardrailsConfig(), name: 'Checks', rules: [] },
          Date.now()
        )
      );
      return;
    }
    if (kind === 'tag') {
      onCreate(
        newJudge(
          makeId(),
          {
            kind: 'tag',
            name: 'Topics and sentiment',
            criteria: [],
            checks: [],
            topics: topics.map((name) => ({
              name: name.slice(0, 80),
              subs: [],
            })),
            rules: [],
            examples: [],
            bar: 80,
            model: DEFAULT_JUDGE_MODEL,
          },
          Date.now()
        )
      );
      return;
    }
    abort.current?.abort();
    const ctrl = new AbortController();
    abort.current = ctrl;
    setBusy(true);
    try {
      const out = await requestJudgeImport(prompt, ctrl.signal);
      onCreate({
        ...newJudge(makeId(), out.config, Date.now()),
        ...(out.description ? { description: out.description } : {}),
      });
    } catch (err) {
      if (!ctrl.signal.aborted) {
        setError(
          err instanceof Error
            ? err.message
            : 'The prompt couldn’t be imported.'
        );
      }
    } finally {
      if (!ctrl.signal.aborted) setBusy(false);
    }
  };

  const ready =
    kind === 'check' ||
    (kind === 'tag' && topics.length > 0) ||
    (kind === 'import' && prompt.trim().length >= 40);

  return (
    <div className="c-validate-panel" test-id="bobcat-validate-new-judge-panel">
      <PanelHead
        title="New judge"
        icon="stars"
        sub="A judge scores this solution’s conversations: test runs now, and live ones once live scoring is connected."
        onClose={onClose}
      />
      <div className="c-validate-panel__scroll">
        <div className="c-validate-field">
          <span>Start from</span>
          <div className="c-judges-opts">
            {NEW_OPTIONS.map((o) => (
              <label key={o.kind} className="c-judges-opt">
                <input
                  type="radio"
                  name="judge-kind"
                  value={o.kind}
                  checked={kind === o.kind}
                  test-id={`bobcat-validate-new-judge-${o.kind}`}
                  onChange={() => setKind(o.kind)}
                />
                <span>
                  <b>{o.label}</b>
                  <span>{o.help}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
        {kind === 'tag' && (
          <label className="c-validate-field">
            <span>Topics, one per line</span>
            <textarea
              rows={5}
              value={topic}
              placeholder={
                'Claims status\nFile a claim\nPolicy & coverage\nBilling'
              }
              test-id="bobcat-validate-new-judge-topics"
              onChange={(e) => setTopic(e.target.value)}
            />
            <small>Add subtopics on the judge’s page.</small>
          </label>
        )}
        {kind === 'import' && (
          <label className="c-validate-field">
            <span>The scorer’s prompt</span>
            <textarea
              rows={10}
              value={prompt}
              placeholder="You are a fair and impartial QA evaluator… ## Category Guidance ### 1. Clear & Concise Delivery — Score options: 10 = Yes, 0 = No…"
              test-id="bobcat-validate-new-judge-prompt"
              onChange={(e) => setPrompt(e.target.value)}
            />
            <small>
              Anything about where results were sent stays behind: here they go
              to Validate.
            </small>
          </label>
        )}
        {error && (
          <p className="c-validate-error" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="c-validate-panel__actions">
        <Button
          size="small"
          variant="primary"
          disabled={!ready || busy}
          test-id="bobcat-validate-new-judge-create"
          onClick={() => void create()}
        >
          {busy ? 'Importing…' : kind === 'import' ? 'Import' : 'Create judge'}
        </Button>
        <Button size="small" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

/* ---- A run's other judges ----------------------------------------------------- */

const RunJudgeLine: FC<{
  judge: ValidateJudge;
  entry: RunJudgeEntry | undefined;
  onRetry: () => void;
}> = ({ judge, entry, onRetry }) => {
  const { config } = judge;
  const fresh = entry && entry.hash === configHash(config) ? entry : undefined;
  let body: React.ReactNode;
  if (!fresh) {
    body = <span className="is-muted">Judging…</span>;
  } else if (fresh.status === 'error') {
    body = (
      <>
        <span className="is-error">{fresh.message}</span>
        <button type="button" className="c-validate-link" onClick={onRetry}>
          Try again
        </button>
      </>
    );
  } else {
    const r: JudgeResult = fresh.result;
    const failed = r.checks.filter((c) => c.failed);
    body =
      r.kind === 'tag' ? (
        <span>
          {topicLabel(r.topic, r.subtopic) || 'No topic'}
          {r.sentiment ? ` · ${r.sentiment}` : ''}
        </span>
      ) : (
        <>
          <span className={`c-judges-value ${r.pass ? 'is-pass' : 'is-fail'}`}>
            {r.kind === 'check' ? (r.pass ? 'Pass' : 'Fail') : `${r.score}/100`}
          </span>
          <span>
            {failed.length
              ? failed.map((c) => `${c.name}: ${c.reason}`).join(' ')
              : r.summary}
          </span>
        </>
      );
  }
  return (
    <div className="c-judges-runline" test-id="bobcat-validate-run-judge">
      <b>{config.name}</b>
      {body}
    </div>
  );
};

/**
 * In a run's detail: what the solution's other judges made of it. Each runs
 * once the conversation is over and scored, and again after the judge
 * changes; the result is kept on the run.
 */
export const RunJudges: FC<{
  run: ValidateRun;
  judges: ValidateJudge[];
  onResult: (runId: string, judgeId: string, entry: RunJudgeEntry) => void;
}> = ({ run, judges, onResult }) => {
  const extra = extraTestJudges(judges);
  const asked = useRef(new Set<string>());
  const finished =
    run.score.status === 'scored' || run.score.status === 'error';
  // Which judges, as configured now: a change re-runs the changed ones.
  const extraKey = extra
    .map((j) => `${j.id}:${configHash(j.config)}`)
    .join('|');

  const judgeIt = (j: ValidateJudge) => {
    const hash = configHash(j.config);
    requestJudge({
      judge: j.config,
      transcript: run.transcript.filter((t) => !t.system),
      ...(run.kind === 'scenario' ? { scenario: run.title } : {}),
      ...(run.expectation ? { expectation: run.expectation } : {}),
    })
      .then((result) =>
        onResult(run.id, j.id, { status: 'done', hash, result, at: Date.now() })
      )
      .catch((err: unknown) =>
        onResult(run.id, j.id, {
          status: 'error',
          hash,
          message: err instanceof Error ? err.message : 'The judge failed.',
        })
      );
  };

  useEffect(() => {
    if (!finished) return;
    extra.forEach((j) => {
      const hash = configHash(j.config);
      const key = `${j.id}:${hash}`;
      if (run.judgeResults?.[j.id]?.hash === hash || asked.current.has(key))
        return;
      asked.current.add(key);
      judgeIt(j);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished, run.id, extraKey]);

  if (!extra.length || !finished) return null;
  return (
    <div
      className="c-validate-check c-judges-run"
      test-id="bobcat-validate-run-judges"
    >
      <span className="c-validate-check__label">Other judges</span>
      {extra.map((j) => (
        <RunJudgeLine
          key={j.id}
          judge={j}
          entry={run.judgeResults?.[j.id]}
          onRetry={() => judgeIt(j)}
        />
      ))}
    </div>
  );
};
